import { AuthErrorField, AuthErrorKind } from '@griply/types';
import { getAuthErrorField, getAuthErrorMessageKey } from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { FormInput } from 'shared/components/ui/form-input';
import { SignupFormFields } from 'shared/types/signup-form';
import { isMinLengthError } from 'shared/utils/is-min-length-error';

import * as Styled from './signup-form.style';
import { useSignupForm } from './use-signup-form';

export type LoginFormProps = {
  onSubmit: (loginData: SignupFormFields) => void;
  isLoading: boolean;
  error?: AuthErrorKind;
};

export const SignupForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const { register, onSubmit: submitForm, errors } = useSignupForm(onSubmit);

  const errorMessageKey = error ? getAuthErrorMessageKey(error) : null;
  const errorMessage = errorMessageKey ? t(errorMessageKey) : undefined;
  const errorField = error ? getAuthErrorField(error) : null;

  const nameErrorMessage = useMemo(() => {
    if (isLoading) {
      return;
    }
    if (errors.name?.message) {
      return t('forms.signup.fields.name.error');
    }

    return undefined;
  }, [errors.name?.message, isLoading, t]);

  const emailErrorMessage = useMemo(() => {
    if (isLoading) {
      return;
    }
    if (errors.email?.message) {
      return t('forms.signup.fields.email.error');
    }

    if (errorField === AuthErrorField.Email) {
      return errorMessage;
    }

    return undefined;
  }, [errorField, errorMessage, errors.email?.message, isLoading, t]);

  const passwordErrorMessage = useMemo(() => {
    if (isLoading) {
      return;
    }
    if (isMinLengthError(errors.password)) {
      return t('auth.errors.weak-password');
    }

    if (errors.password?.message) {
      return t('forms.signup.fields.password.error');
    }

    if (errorField === AuthErrorField.Password) {
      return errorMessage;
    }

    return undefined;
  }, [errorField, errorMessage, errors.password, isLoading, t]);

  const formErrorMessage =
    !isLoading && errorField === AuthErrorField.Form ? errorMessage : undefined;

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.Fields>
        <Styled.FieldContainer>
          <FormInput
            {...register('name')}
            id="name"
            autoComplete="name"
            placeholder={t('forms.signup.fields.name.placeholder')}
            error={nameErrorMessage}
            disabled={isLoading}
          />
          {!!nameErrorMessage && (
            <Styled.ErrorMessage>{nameErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>

        <Styled.FieldContainer>
          <FormInput
            {...register('email')}
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('forms.signup.fields.email.placeholder')}
            error={emailErrorMessage}
            disabled={isLoading}
          />
          {!!emailErrorMessage && (
            <Styled.ErrorMessage>{emailErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>

        <Styled.FieldContainer>
          <FormInput
            {...register('password')}
            id="password"
            type="password"
            autoComplete="new-password"
            placeholder={t('forms.signup.fields.password.placeholder')}
            error={passwordErrorMessage}
            disabled={isLoading}
          />
          {!!passwordErrorMessage && (
            <Styled.ErrorMessage>{passwordErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>
      </Styled.Fields>

      {!!formErrorMessage && (
        <Styled.ErrorMessage role="alert">
          {formErrorMessage}
        </Styled.ErrorMessage>
      )}

      <Button
        variant={Variants.Contained}
        color={Colors.Primary}
        size={Sizes.Large}
        type="submit"
        disabled={isLoading}
        fullWidth
        alignCenter
        disabledBackgroundOnly
      >
        {isLoading ? (
          <Styled.Loader color="primary" size={20} />
        ) : (
          t('forms.signup.buttons.sign-in.label')
        )}
      </Button>
    </Styled.Form>
  );
};
